import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import {
  allowInsecureOidc,
  authority,
  keycloak,
  redirectUri,
  secureContext,
} from '@/config'
import { i18n } from '@/i18n'

/**
 * Keycloak authentication, authorization-code flow with PKCE.
 *
 * The panel never sees a password: the user is redirected to Keycloak, comes back
 * with a code, and the library exchanges it for tokens. No credential is created or
 * stored by this application — identity stays in Keycloak.
 *
 * Roles are read from the token but never trusted for anything but presentation —
 * every rule in the role matrix is also enforced by the server.
 *
 * <p><b>PKCE needs a secure context.</b> The code challenge is a SHA-256 of the
 * verifier and `crypto.subtle` — the only thing in the browser that can compute
 * one — is undefined on a plain-HTTP origin. `signinRedirect` therefore throws
 * "Crypto.subtle is available only in secure contexts (HTTPS)" *before* it
 * navigates anywhere, which looks from the outside exactly like a panel that does
 * nothing at all: no Keycloak page, no API call, a blank screen. HTTPS is the fix;
 * `VITE_OIDC_ALLOW_INSECURE=true` is the escape hatch for a pilot server that has
 * no certificate yet, and it drops PKCE rather than pretending to have it.
 */

const disablePKCE = !secureContext && allowInsecureOidc

const settings = {
  authority,
  client_id: keycloak.clientId,
  redirect_uri: redirectUri,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  // "organization": Keycloak puts the organization claim (the user's organization aliases) into the
  // token only when this scope is asked for; mdm-service picks the organization from that claim.
  scope: 'openid profile email organization',
  // Renew silently before expiry so a long shift on the dashboard is not
  // interrupted by a redirect. Keycloak issues a refresh token for this flow, so
  // the renewal is a token call and needs no hidden iframe.
  automaticSilentRenew: true,
  // Session state in sessionStorage: closing the tab ends the session, which is
  // what a shared admin workstation wants.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  // The signin request's own state (verifier, nonce) has to survive the redirect
  // to Keycloak and back. sessionStorage keeps it per tab, same as the user.
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  disablePKCE,
}

const userManager = new UserManager(settings)

if (disablePKCE) {
  console.warn(
    'PKCE is disabled because the panel is served over plain HTTP. ' +
      'Serve it over HTTPS and rebuild without VITE_OIDC_ALLOW_INSECURE.',
  )
}

let currentUser: User | null = null

userManager.events.addUserLoaded((user) => {
  currentUser = user
})

userManager.events.addUserUnloaded(() => {
  currentUser = null
})

userManager.events.addSilentRenewError((error) => {
  // A failed renew is not fatal on its own; the next API call returns 401 and the
  // router sends the user through login again.
  console.warn('Silent token renew failed', error)
})

/**
 * Starts the login redirect.
 *
 * <p>Rejects rather than swallowing: a login that cannot even begin — an
 * unreachable Keycloak, a realm that does not exist, a non-secure origin — is the
 * single most likely reason this panel shows nothing, and it has to reach a screen
 * the operator can read.
 */
export async function login(returnTo?: string): Promise<void> {
  await userManager.signinRedirect({
    state: returnTo ?? window.location.pathname,
    // The Keycloak page (keycloak-theme/) opens in the language the console is in.
    ui_locales: i18n.global.locale.value,
  })
}

/** Completes the login redirect; returns where the user was heading. */
export async function completeLogin(): Promise<string> {
  const user = await userManager.signinRedirectCallback()
  currentUser = user
  return typeof user.state === 'string' ? user.state : '/dashboard'
}

export async function logout(): Promise<void> {
  await userManager.signoutRedirect()
}

/** The signed-in user, or null. Reads from storage on first call after a reload. */
export async function getUser(): Promise<User | null> {
  if (!currentUser) {
    currentUser = await userManager.getUser()
  }
  return currentUser
}

/**
 * Bearer token for an API call, or null when nobody is signed in.
 *
 * <p>An expired token is renewed here rather than reported as "not signed in":
 * the access token's lifetime in Keycloak is minutes, the refresh token's is the
 * shift, and a tab left open over lunch should keep working without a redirect.
 */
export async function getAccessToken(): Promise<string | null> {
  const user = await getUser()
  if (!user) {
    return null
  }
  if (!user.expired) {
    return user.access_token
  }
  try {
    const renewed = await userManager.signinSilent()
    currentUser = renewed
    return renewed?.access_token ?? null
  } catch (error) {
    console.warn('Token renew on demand failed', error)
    return null
  }
}

/** Drops the local session so the next navigation goes through Keycloak again. */
export async function forgetUser(): Promise<void> {
  currentUser = null
  await userManager.removeUser()
}

/**
 * Keycloak roles, read from the **access token** and normalised for the console:
 * upper case, dashes to underscores, ROLE_ prefix — `mdm-org-admin` becomes
 * `ROLE_MDM_ORG_ADMIN`.
 *
 * <p>The access token, not `user.profile`. `profile` is the ID token's claims, and
 * Keycloak puts `realm_access` in the access token only unless somebody adds a
 * mapper for the ID token as well. Reading `profile.realm_access` therefore found
 * nothing for a user who genuinely held ROLE_MDM_ORG_ADMIN, and the console sent
 * them to /no-role while every API call they made succeeded. The access token is
 * also the right source on principle: it is the token the resource server
 * authorises, so the panel now hides exactly what the server would refuse.
 *
 * <p>Realm roles and client roles (`resource_access.<client>.roles`) together —
 * exactly the two sources mdm-service's `JwtAuthConverter` merges, so the console
 * and the server never disagree about who may do what.
 */
export async function getRoles(): Promise<string[]> {
  const user = await getUser()
  if (!user) {
    return []
  }
  // The ID token is the fallback for a realm that maps realm_access into it too;
  // it is not the primary source and is empty in a default Keycloak.
  const claims = decodeJwtPayload(user.access_token) ?? (user.profile as Record<string, unknown>)
  const realmAccess = claims.realm_access as { roles?: string[] } | undefined
  const resourceAccess = (claims.resource_access ?? {}) as Record<string, { roles?: string[] }>

  const raw = [
    ...(realmAccess?.roles ?? []),
    ...Object.values(resourceAccess).flatMap((client) => client.roles ?? []),
  ]
  const normalised = raw.map((role) => {
    const upper = role.toUpperCase().replace(/-/g, '_')
    return upper.startsWith('ROLE_') ? upper : `ROLE_${upper}`
  })
  return [...new Set(normalised)]
}

/**
 * Aliases of the Keycloak organizations the user belongs to — the `organization`
 * claim, which Keycloak writes as a list of aliases, or as a map keyed by alias
 * when the mapper also includes ids. mdm-service reads the same claim to decide
 * which organization a request acts in.
 */
export async function getOrganizations(): Promise<string[]> {
  const user = await getUser()
  if (!user) {
    return []
  }
  const claims = decodeJwtPayload(user.access_token) ?? (user.profile as Record<string, unknown>)
  const claim = claims.organization
  if (Array.isArray(claim)) {
    return claim.map(String)
  }
  if (claim && typeof claim === 'object') {
    return Object.keys(claim)
  }
  return typeof claim === 'string' ? [claim] : []
}

/**
 * The claims of a JWT, without verifying it.
 *
 * <p>Safe here and only here: these claims decide what the panel *draws*. Every
 * rule they express is enforced again by the server against the same token, which
 * does verify the signature — so a forged token buys a menu entry and a 403.
 */
function decodeJwtPayload(token: string | undefined): Record<string, unknown> | null {
  const payload = token?.split('.')[1]
  if (!payload) {
    return null
  }
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>
  } catch (error) {
    console.warn('Access token could not be read for its claims', error)
    return null
  }
}

export { userManager }
